import React, { useEffect, useState } from "react";
import {
  DataGridPro,
  GridToolbarContainer,
  LicenseInfo,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from "@mui/x-data-grid-pro";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { connect } from "react-redux";
import _, { get, isEmpty, includes, filter } from "lodash/fp";
import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { Typography, Tooltip, Box } from "@material-ui/core";
import { Delete, Edit, FactCheck, Gavel } from "@mui/icons-material";
import { useHistory } from "react-router";

import { ADMIN } from "../../../../consts/roles";
import { getArbitratorConflictGridCell } from "../../../../utils/arbitratorUtils";
import TeamName, { teamNameTextOnly } from "components/team/TeamName";
import { CustomTooltip, useLocalStorage } from "../../../helpers";
import { isColumnHidden } from "../../../../utils/columnUtils";
import { runPairing, deletePairing } from "../../../../actions/adminActions";
import { Button, Alert } from "@mui/material";
import { getCurrentMoot } from "../../../../reducers/mootSelectors";
import InfoIcon from "@mui/icons-material/Info";
import * as adminSelectors from "../../../../reducers/adminSelectors";
import { getCompleteOralArgumentSchedule } from "../../../../reducers/adminSelectors";

LicenseInfo.setLicenseKey(
  "19a99a1195c5f52fabd5f3310b083226T1JERVI6Mjk5MzAsRVhQSVJZPTE2NjQxNDA4NTIwMDAsS0VZVkVSU0lPTj0x",
);

const validatePairings = (schedule) => {
  const rows = [];
  const violationMap = {};

  const addViolation = (
    teamId,
    school,
    claimantCount,
    respondentCount,
    totalCount,
    status,
  ) => {
    if (!violationMap[teamId]) {
      violationMap[teamId] = {
        teamId,
        school,
        claimantCount,
        respondentCount,
        totalCount,
        statuses: [],
      };
    }
    violationMap[teamId].statuses.push(status);
  };

  // Appearance counts — check each role's practice flag independently
  // so a non-practice appearance isn't missed in a mixed-practice pairing
  const teamMap = {};
  schedule.forEach((p) => {
    if (p.claimantId && !p.claimantPractice) {
      if (!teamMap[p.claimantId]) {
        teamMap[p.claimantId] = {
          id: p.claimantId,
          school: p.claimant?.school ?? `Team ${p.claimantId}`,
          jurisdiction: p.claimant?.jurisdiction ?? null,
          claimantCount: 0,
          respondentCount: 0,
        };
      }
      teamMap[p.claimantId].claimantCount++;
    }
    if (p.respondentId && !p.respondentPractice) {
      if (!teamMap[p.respondentId]) {
        teamMap[p.respondentId] = {
          id: p.respondentId,
          school: p.respondent?.school ?? `Team ${p.respondentId}`,
          jurisdiction: p.respondent?.jurisdiction ?? null,
          claimantCount: 0,
          respondentCount: 0,
        };
      }
      teamMap[p.respondentId].respondentCount++;
    }
  });

  // Track practice appearances for teams with competitive rounds,
  // so missing-role violations can note when a practice round exists
  schedule.forEach((p) => {
    if (p.claimantId && p.claimantPractice && teamMap[p.claimantId]) {
      teamMap[p.claimantId].claimantPracticeCount =
        (teamMap[p.claimantId].claimantPracticeCount || 0) + 1;
    }
    if (p.respondentId && p.respondentPractice && teamMap[p.respondentId]) {
      teamMap[p.respondentId].respondentPracticeCount =
        (teamMap[p.respondentId].respondentPracticeCount || 0) + 1;
    }
  });

  Object.values(teamMap).forEach((t) => {
    const totalCount = t.claimantCount + t.respondentCount;
    if (t.claimantCount !== 2) {
      const hasPractice = (t.claimantPracticeCount || 0) > 0;
      addViolation(
        t.id,
        t.school,
        t.claimantCount,
        t.respondentCount,
        totalCount,
        t.claimantCount < 2
          ? `Missing claimant appearance(s)${hasPractice ? " (has a practice round)" : ""}`
          : "Too many claimant appearances",
      );
    }
    if (t.respondentCount !== 2) {
      const hasPractice = (t.respondentPracticeCount || 0) > 0;
      addViolation(
        t.id,
        t.school,
        t.claimantCount,
        t.respondentCount,
        totalCount,
        t.respondentCount < 2
          ? `Missing respondent appearance(s)${hasPractice ? " (has a practice round)" : ""}`
          : "Too many respondent appearances",
      );
    }
  });

  // Unique opponents — per team, only counting non-practice appearances
  const teamOpponentMap = {};
  schedule.forEach((p) => {
    if (!p.claimantId || !p.respondentId) return;
    if (!p.claimantPractice) {
      if (!teamOpponentMap[p.claimantId]) teamOpponentMap[p.claimantId] = {};
      teamOpponentMap[p.claimantId][p.respondentId] =
        (teamOpponentMap[p.claimantId][p.respondentId] || 0) + 1;
    }
    if (!p.respondentPractice) {
      if (!teamOpponentMap[p.respondentId])
        teamOpponentMap[p.respondentId] = {};
      teamOpponentMap[p.respondentId][p.claimantId] =
        (teamOpponentMap[p.respondentId][p.claimantId] || 0) + 1;
    }
  });

  Object.entries(teamOpponentMap).forEach(([teamId, opponents]) => {
    const parsedId = parseInt(teamId);
    const hasRepeat = Object.values(opponents).some((count) => count > 1);
    if (hasRepeat) {
      const t = teamMap[parsedId];
      const totalCount = t ? t.claimantCount + t.respondentCount : null;
      addViolation(
        parsedId,
        t?.school ?? `Team ${teamId}`,
        t?.claimantCount ?? null,
        t?.respondentCount ?? null,
        totalCount,
        "Multiple pairings against same team",
      );
    }
  });

  // Same-day appearances
  const teamDayMap = {};
  schedule.forEach((p) => {
    if (!p.pairingTime) return;
    const day = format(utcToZonedTime(p.pairingTime, "Asia/Hong_Kong"), "EEEE");
    if (p.claimantId && !p.claimantPractice) {
      if (!teamDayMap[p.claimantId])
        teamDayMap[p.claimantId] = {
          school: p.claimant?.school ?? `Team ${p.claimantId}`,
          days: {},
        };
      teamDayMap[p.claimantId].days[day] =
        (teamDayMap[p.claimantId].days[day] || 0) + 1;
    }
    if (p.respondentId && !p.respondentPractice) {
      if (!teamDayMap[p.respondentId])
        teamDayMap[p.respondentId] = {
          school: p.respondent?.school ?? `Team ${p.respondentId}`,
          days: {},
        };
      teamDayMap[p.respondentId].days[day] =
        (teamDayMap[p.respondentId].days[day] || 0) + 1;
    }
  });

  Object.entries(teamDayMap).forEach(([teamId, { school, days }]) => {
    const t = teamMap[teamId];
    const totalCount = t ? t.claimantCount + t.respondentCount : null;
    Object.entries(days).forEach(([day, count]) => {
      if (count === 2) {
        rows.push({
          id: `day-warning-${teamId}-${day}`,
          teamId: parseInt(teamId),
          school,
          claimantCount: t?.claimantCount ?? null,
          respondentCount: t?.respondentCount ?? null,
          totalCount,
          status: `Warning: Team is scheduled twice on ${day}`,
          isWarning: true,
        });
      } else if (count > 2) {
        addViolation(
          parseInt(teamId),
          school,
          t?.claimantCount ?? null,
          t?.respondentCount ?? null,
          totalCount,
          `Team is scheduled ${count} times in the same day`,
        );
      }
    });
  });

  // Jurisdiction checks: own-jurisdiction pairings and 3+ opponents from same jurisdiction
  const teamOpponentJurisdictionMap = {};
  schedule.forEach((p) => {
    if (!p.claimantId || !p.respondentId) return;
    if (!p.claimantPractice) {
      const opponentJurisdiction = p.respondent?.jurisdiction;
      if (opponentJurisdiction) {
        if (!teamOpponentJurisdictionMap[p.claimantId])
          teamOpponentJurisdictionMap[p.claimantId] = {};
        teamOpponentJurisdictionMap[p.claimantId][opponentJurisdiction] =
          (teamOpponentJurisdictionMap[p.claimantId][opponentJurisdiction] ||
            0) + 1;
      }
    }
    if (!p.respondentPractice) {
      const opponentJurisdiction = p.claimant?.jurisdiction;
      if (opponentJurisdiction) {
        if (!teamOpponentJurisdictionMap[p.respondentId])
          teamOpponentJurisdictionMap[p.respondentId] = {};
        teamOpponentJurisdictionMap[p.respondentId][opponentJurisdiction] =
          (teamOpponentJurisdictionMap[p.respondentId][opponentJurisdiction] ||
            0) + 1;
      }
    }
  });

  Object.entries(teamOpponentJurisdictionMap).forEach(
    ([teamId, jurisdictions]) => {
      const parsedId = parseInt(teamId);
      const t = teamMap[parsedId];
      const totalCount = t ? t.claimantCount + t.respondentCount : null;
      Object.entries(jurisdictions).forEach(([jurisdiction, count]) => {
        if (t?.jurisdiction && jurisdiction === t.jurisdiction) {
          addViolation(
            parsedId,
            t?.school ?? `Team ${teamId}`,
            t?.claimantCount ?? null,
            t?.respondentCount ?? null,
            totalCount,
            count === 1
              ? `Paired against own jurisdiction (${jurisdiction})`
              : `Paired against own jurisdiction (${jurisdiction}) ${count} times`,
          );
        } else if (count >= 3) {
          addViolation(
            parsedId,
            t?.school ?? `Team ${teamId}`,
            t?.claimantCount ?? null,
            t?.respondentCount ?? null,
            totalCount,
            `Paired against ${jurisdiction} jurisdiction ${count} times`,
          );
        }
      });
    },
  );

  Object.values(violationMap).forEach((v) => {
    rows.push({
      id: `violation-${v.teamId}`,
      teamId: v.teamId,
      school: v.school,
      claimantCount: v.claimantCount,
      respondentCount: v.respondentCount,
      totalCount: v.totalCount,
      status: v.statuses.join(", "),
      isWarning: false,
    });
  });

  // Sort violations (isWarning: false) before warnings (isWarning: true)
  return _.sortBy("isWarning", rows);
};

const violationColumns = [
  {
    field: "teamId",
    headerName: "School Id",
    width: 90,
    align: "right",
    headerAlign: "right",
  },
  { field: "school", headerName: "School", flex: 2 },
  {
    field: "claimantCount",
    headerName: "Claimant Count",
    width: 170,
    align: "right",
    headerAlign: "right",
  },
  {
    field: "respondentCount",
    headerName: "Respondent Count",
    width: 185,
    align: "right",
    headerAlign: "right",
  },
  {
    field: "totalCount",
    headerName: "Total",
    width: 80,
    align: "right",
    headerAlign: "right",
  },
  {
    field: "status",
    headerName: "Status",
    flex: 2,
    renderCell: (params) => (
      <div
        style={{
          whiteSpace: "normal",
          wordBreak: "break-word",
          padding: "8px 0",
        }}
      >
        {params.value}
      </div>
    ),
  },
];

const ScheduleTable = ({
  user = {},
  moot,
  dateFilter,
  runPairing = _.noop,
  oralArgumentSchedule,
  allArbitrators,
  getSelectedRoundSchedule = _.noop,
  deletePairing = _.noop,
  getOralArgumentSchedule = _.noop,
}) => {
  const history = useHistory();

  const [isLoading, setIsLoading] = React.useState(false);
  const [pairingButtonEnabled, setPairingButtonEnabled] = React.useState(
    !moot?.arbitratorPairingsAreBeingGenerated,
  );

  const [generalRoundsColumns, setGeneralRoundsColumns] = useLocalStorage(
    "generalRoundsColumns",
    "",
  );

  const onEdit = (pairingId) => {
    history.push(`/admin/oral-arguments/edit/${pairingId}`);
  };
  const onCreate = () => {
    history.push(`/admin/oral-arguments/new`);
  };
  const onInputScores = (pairingId) => {
    history.push(`/admin/oral-arguments/score/${pairingId}`);
  };
  const onReviewScores = (pairingId) => {
    history.push(`/admin/oral-arguments/review-score/${pairingId}`);
  };

  const [selectedRoundSchedule, setSelectedRoundSchedule] = useState([]);
  useEffect(() => {
    const asyncFetchData = async () => {
      const result = getSelectedRoundSchedule();
      setSelectedRoundSchedule(result);
    };
    asyncFetchData();
  }, [oralArgumentSchedule]);

  const validationSectionRef = React.useRef(null);
  const scrollToValidation = () =>
    validationSectionRef.current?.scrollIntoView({ behavior: "smooth" });

  const handleColumnVisibilityChange = (params) => {
    setGeneralRoundsColumns(params);
  };

  const violations =
    isEmpty(dateFilter) || dateFilter === "All"
      ? validatePairings(selectedRoundSchedule)
      : [];

  const CustomToolbar = () => {
    return (
      <GridToolbarContainer
        style={{ justifyContent: "space-between", alignContent: "center" }}
      >
        <div>
          <GridToolbarColumnsButton className="toolbar-button" />
          <GridToolbarFilterButton className="toolbar-button" />
          <GridToolbarDensitySelector className="toolbar-button" />
          <GridToolbarExport
            className="toolbar-button"
            printOptions={{ disableToolbarButton: true }}
            csvOptions={{
              fileName: `General Round Schedule - ${format(new Date(), "yyyy-MM-dd")}`,
              utf8WithBom: true,
            }}
          />
        </div>
        <div>
          <Typography
            style={{
              margin: "-2px 0 0",
              padding: "2px 0.25rem",
              textAlign: "left",
              fontSize: "11px",
              color: "#000000",
              backgroundColor: `${moot.showOralDetailsTeamOpenDate.notYetOpen === true ? "#FFFF8A" : "#B9E9C9"}`,
            }}
          >
            General Rounds schedule{" "}
            <strong>
              {moot.showOralDetailsTeamOpenDate.notYetOpen === true
                ? "is not"
                : "is"}{" "}
            </strong>{" "}
            visible to Teams.
          </Typography>
          {/*
                <Typography style={{margin:'-2px 0 0 4px', padding:'2px 0.25rem', textAlign:'left', fontSize:'11px', color:'#000000', backgroundColor:`${moot.showOralDetailsArbitratorsOpenDate.notYetOpen === true ? '#FFFF8A' : '#B9E9C9'}`}}>General Rounds schedule <strong>{moot.showOralDetailsArbitratorsOpenDate.notYetOpen === true ? 'is not' : 'is'} </strong> visible to Arbitrators.
                </Typography>
                 */}
        </div>
        <div>
          {includes(ADMIN, user.roles) && (
            <>
              <Button
                style={{
                  margin: "0 2rem 0 0",
                }}
                color="primary"
                size="small"
                disabled={!pairingButtonEnabled}
                title=""
                onClick={() => onCreate()}
              >
                Add pairing
              </Button>
              <Button
                color="primary"
                size="small"
                disabled={!pairingButtonEnabled}
                title=""
                onClick={async (e) => {
                  e.preventDefault();
                  const result = window.confirm(
                    `This will replace all arbitrator pairings. Are you sure you want to continue?`,
                  );
                  if (result) {
                    try {
                      setIsLoading(true);
                      //fire and forget because this takes a long time
                      runPairing();
                    } finally {
                      setIsLoading(false);
                      setPairingButtonEnabled(false);
                    }
                  }
                }}
              >
                {pairingButtonEnabled
                  ? "Pair Arbitrators"
                  : "Generating Arbitrator Pairings"}
              </Button>
            </>
          )}
          {!pairingButtonEnabled && (
            <span>
              <Tooltip title="The arbitrator pairing process may take a few minutes to run. An email notification will be sent to the admins upon pairing completion.">
                <InfoIcon sx={{ fontSize: 14, transform: "translateY(2px)" }} />
              </Tooltip>
            </span>
          )}
        </div>
      </GridToolbarContainer>
    );
  };

  const getArbitratorRecord = (arbitrator) => {
    const arbWithSchedule = _.find(
      (a) => arbitrator.id === a.id,
      allArbitrators,
    );

    return {
      ...arbitrator,
      schedule: arbWithSchedule?.schedule,
    };
  };
  const columns = [
    {
      field: "id",
      headerName: "Id",
      align: "right",
      flex: 1,
      hide: isColumnHidden(generalRoundsColumns.id, true),
    },
    {
      field: "claimantId",
      headerName: "Claimant Id",
      align: "right",
      flex: 1,
      hide: isColumnHidden(generalRoundsColumns.claimantId, true),
    },
    {
      field: "claimant",
      headerName: "Claimant",
      flex: 1,
      hide: isColumnHidden(generalRoundsColumns.claimant, false),
      renderCell: (params) => (
        <TeamName team={params.row.claimant} adminUse={true} />
      ),
      valueGetter: (params) =>
        params.row.claimant?.hideTeamName === true
          ? `TEAM ID ${params.row.claimant?.id}`
          : params.row.claimant?.school,
    },
    {
      field: "claimantPractice",
      headerName: "Claimant Practice?",
      flex: 1,
      hide: isColumnHidden(generalRoundsColumns.claimantPractice, true),
      valueGetter: (params) => params.row.claimantPractice,
    },
    {
      field: "respondentId",
      headerName: "Respondent Id",
      align: "right",
      flex: 1,
      hide: isColumnHidden(generalRoundsColumns.respondentId, true),
    },
    {
      field: "respondent",
      headerName: "Respondent",
      flex: 1,
      hide: isColumnHidden(generalRoundsColumns.respondent, false),
      renderCell: (params) => (
        <TeamName team={params.row.respondent} adminUse={true} />
      ),
      valueGetter: (params) =>
        params.row.respondent?.hideTeamName === true
          ? `TEAM ID ${params.row.respondent?.id}`
          : params.row.respondent?.school,
    },
    {
      field: "respondentPractice",
      headerName: "Respondent Practice?",
      flex: 1,
      hide: isColumnHidden(generalRoundsColumns.respondentPractice, true),
      valueGetter: (params) => params.row.respondentPractice,
    },
    {
      field: "pairingDate",
      headerName: "Date HKT",
      align: "right",
      width: 130,
      hide: isColumnHidden(generalRoundsColumns.pairingDate, false),
      renderCell: (params) =>
        format(
          utcToZonedTime(params.row.pairingTime, "Asia/Hong_Kong"),
          "iii, dd MMMM",
        ),
      valueGetter: (params) =>
        format(
          utcToZonedTime(params.row.pairingTime, "Asia/Hong_Kong"),
          "yyyy/MM/dd",
        ),
    },
    {
      field: "pairingTime",
      headerName: "Time HKT",
      align: "right",
      width: 100,
      hide: isColumnHidden(generalRoundsColumns.pairingTime, false),
      valueGetter: (params) =>
        format(
          utcToZonedTime(params.row.pairingTime, "Asia/Hong_Kong"),
          "hh:mm aaa",
        ),
    },
    {
      field: "pairingDateTime",
      headerName: "Date & Time HKT",
      align: "right",
      flex: 1,
      hide: isColumnHidden(generalRoundsColumns.pairingDateTime, true),
      valueGetter: (params) =>
        format(
          utcToZonedTime(params.row.pairingTime, "Asia/Hong_Kong"),
          "yyyy/MM/dd HH:mm",
        ),
    },
    {
      field: "hearingLink",
      headerName: "Virtual Hearing Link",
      flex: 1,
      hide: isColumnHidden(generalRoundsColumns.hearingLink, true),
      sortable: false,
      renderCell: (params) => {
        if (!params.row.hearingLink) {
          return <span>coming soon</span>;
        } else {
          return (
            <a
              target="_blank"
              rel="noreferrer"
              href={`${params.row.hearingLink}`}
            >
              Click Here
            </a>
          );
        }
      },
    },
    {
      field: "room",
      headerName: "Room",
      flex: 1,
      hide: isColumnHidden(generalRoundsColumns.room, false),
      sortable: false,
      renderCell: (params) => {
        if (!get("row.room", params)) {
          return <CustomTooltip content="available at check-in" />;
        } else {
          return <CustomTooltip content={params.row.room} />;
        }
      },
    },
    {
      field: "chairArbitratorId",
      headerName: "First Arbitrator Id",
      align: "right",
      flex: 1,
      hide: isColumnHidden(generalRoundsColumns.chairArbitratorId, true),
      valueGetter: (params) => `${params.row.chairArbitrator?.id ?? ""}`,
    },
    {
      field: "chairArbitrator",
      headerName: "First Arbitrator",
      flex: 1,
      hide: isColumnHidden(generalRoundsColumns.chairArbitrator, false),
      valueGetter: (params) =>
        `${params.row.chairArbitrator?.firstName ?? ""} ${params.row.chairArbitrator?.lastName ?? ""}`.trim(),
      renderCell: (params) =>
        getArbitratorConflictGridCell(
          params.row.claimant,
          params.row.respondent,
          getArbitratorRecord(params.row?.chairArbitrator),
          params.row.pairingTime,
          oralArgumentSchedule,
          params.row.id,
          user.roles,
        ),
    },
    {
      field: "secondArbitratorId",
      headerName: "Second Arbitrator Id",
      align: "right",
      flex: 1,
      hide: isColumnHidden(generalRoundsColumns.secondArbitratorId, true),
      valueGetter: (params) => `${params.row.secondArbitrator?.id ?? ""}`,
    },
    {
      field: "secondArbitrator",
      headerName: "Second Arbitrator",
      flex: 1,
      hide: isColumnHidden(generalRoundsColumns.secondArbitrator, false),
      valueGetter: (params) =>
        `${params.row.secondArbitrator?.firstName ?? ""} ${params.row.secondArbitrator?.lastName ?? ""}`.trim(),
      renderCell: (params) =>
        getArbitratorConflictGridCell(
          params.row.claimant,
          params.row.respondent,
          getArbitratorRecord(params.row?.secondArbitrator),
          params.row.pairingTime,
          oralArgumentSchedule,
          params.row.id,
          user.roles,
        ),
    },
    {
      field: "thirdArbitratorId",
      headerName: "Third Arbitrator Id",
      align: "right",
      flex: 1,
      hide: isColumnHidden(generalRoundsColumns.thirdArbitratorId, true),
      valueGetter: (params) => `${params.row.thirdArbitrator?.id ?? ""}`,
    },
    {
      field: "thirdArbitrator",
      headerName: "Third Arbitrator",
      flex: 1,
      hide: isColumnHidden(generalRoundsColumns.thirdArbitrator, false),
      valueGetter: (params) =>
        `${params.row.thirdArbitrator?.firstName ?? ""} ${params.row.thirdArbitrator?.lastName ?? ""}`.trim(),
      renderCell: (params) =>
        getArbitratorConflictGridCell(
          params.row.claimant,
          params.row.respondent,
          getArbitratorRecord(params.row?.thirdArbitrator),
          params.row.pairingTime,
          oralArgumentSchedule,
          params.row.id,
          user.roles,
        ),
    },
    {
      field: "firstReserveArbitratorId",
      headerName: "First Reserve Id",
      align: "right",
      flex: 1,
      hide: isColumnHidden(generalRoundsColumns.firstReserveArbitratorId, true),
      valueGetter: (params) => `${params.row.firstReserveArbitrator?.id ?? ""}`,
    },
    {
      field: "firstReserveArbitrator",
      headerName: "First Reserve",
      flex: 1,
      hide: isColumnHidden(generalRoundsColumns.firstReserveArbitrator, true),
      valueGetter: (params) =>
        `${params.row.firstReserveArbitrator?.firstName ?? ""} ${params.row.firstReserveArbitrator?.lastName ?? ""}`.trim(),
      renderCell: (params) =>
        getArbitratorConflictGridCell(
          params.row.claimant,
          params.row.respondent,
          getArbitratorRecord(params.row?.firstReserveArbitrator),
          params.row.pairingTime,
          oralArgumentSchedule,
          params.row.id,
          user.roles,
        ),
    },
    {
      field: "secondReserveArbitratorId",
      headerName: "Second Reserve Id",
      align: "right",
      flex: 1,
      hide: isColumnHidden(
        generalRoundsColumns.secondReserveArbitratorId,
        true,
      ),
      valueGetter: (params) =>
        `${params.row.secondReserveArbitrator?.id ?? ""}`,
    },
    {
      field: "secondReserveArbitrator",
      headerName: "Second Reserve",
      flex: 1,
      hide: isColumnHidden(generalRoundsColumns.secondReserveArbitrator, true),
      valueGetter: (params) =>
        `${params.row.secondReserveArbitrator?.firstName ?? ""} ${params.row.secondReserveArbitrator?.lastName ?? ""}`.trim(),
      renderCell: (params) =>
        getArbitratorConflictGridCell(
          params.row.claimant,
          params.row.respondent,
          getArbitratorRecord(params.row?.secondReserveArbitrator),
          params.row.pairingTime,
          oralArgumentSchedule,
          params.row.id,
          user.roles,
        ),
    },
    {
      field: "thirdReserveArbitratorId",
      headerName: "Third Reserve Id",
      align: "right",
      flex: 1,
      hide: isColumnHidden(generalRoundsColumns.thirdReserveArbitratorId, true),
      valueGetter: (params) => `${params.row.thirdReserveArbitrator?.id ?? ""}`,
    },
    {
      field: "thirdReserveArbitrator",
      headerName: "Third Reserve",
      flex: 1,
      hide: isColumnHidden(generalRoundsColumns.thirdReserveArbitrator, true),
      valueGetter: (params) =>
        `${params.row.thirdReserveArbitrator?.firstName ?? ""} ${params.row.thirdReserveArbitrator?.lastName ?? ""}`.trim(),
      renderCell: (params) =>
        getArbitratorConflictGridCell(
          params.row.claimant,
          params.row.respondent,
          getArbitratorRecord(params.row?.thirdReserveArbitrator),
          params.row.pairingTime,
          oralArgumentSchedule,
          params.row.id,
          user.roles,
        ),
    },
    {
      field: "claimantScore",
      headerName: "Claimant Score",
      align: "right",
      width: 130,
      hide: isColumnHidden(generalRoundsColumns.claimantScore, false),
      valueGetter: (params) => {
        const score = params.row.PairingScore;
        if (isEmpty(score)) return 0;
        return (
          score.firstClaimantChairScore +
          score.firstClaimantArb1Score +
          score.firstClaimantArb2Score +
          score.secondClaimantChairScore +
          score.secondClaimantArb1Score +
          score.secondClaimantArb2Score
        );
      },
    },
    {
      field: "respondentScore",
      headerName: "Respondent Score",
      align: "right",
      width: 130,
      hide: isColumnHidden(generalRoundsColumns.respondentScore, false),
      valueGetter: (params) => {
        const score = params.row.PairingScore;
        if (isEmpty(score)) return 0;
        return (
          score.firstRespondentChairScore +
          score.firstRespondentArb1Score +
          score.firstRespondentArb2Score +
          score.secondRespondentChairScore +
          score.secondRespondentArb1Score +
          score.secondRespondentArb2Score
        );
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Action",
      flex: 1,
      hide: isColumnHidden(generalRoundsColumns.actions, false),
      getActions: (params) => [
        <GridActionsCellItem
          label="Edit"
          onClick={() => onEdit(params.row.id)}
          disabled={!includes(ADMIN, user.roles)}
          icon={
            <Tooltip title="Edit">
              <Edit />
            </Tooltip>
          }
        />,
        <GridActionsCellItem
          label="Input Scores"
          onClick={() => onInputScores(params.row.id)}
          icon={
            <Tooltip title="Input Scores">
              <Gavel />
            </Tooltip>
          }
        />,
        <GridActionsCellItem
          label="Review Scores"
          onClick={() => onReviewScores(params.row.id)}
          icon={
            <Tooltip title="Review Scores">
              <FactCheck />
            </Tooltip>
          }
        />,
      ],
    },
    {
      field: "delete",
      hide: isColumnHidden(generalRoundsColumns.delete, true),
      type: "actions",
      headerName: "Delete",
      getActions: (params) => [
        <GridActionsCellItem
          icon={
            <Tooltip title="Delete">
              <Delete />
            </Tooltip>
          }
          onClick={async () => {
            const result = window.confirm(
              `This will permanently delete the pairing record and any scores associated for ${teamNameTextOnly(params.row?.claimant)} vs ${teamNameTextOnly(params.row?.respondent)}. Are you sure you want to continue?`,
            );
            if (result) {
              try {
                setIsLoading(true);
                await deletePairing(params.row.id);
                await getOralArgumentSchedule(true);
              } finally {
                setIsLoading(false);
              }
            }
          }}
          label="Delete"
          disabled={!includes(ADMIN, user.roles)}
        />,
      ],
    },
  ];

  const getFilteredSchedule = (selectedRoundSchedule, dateFilter) => {
    if (isEmpty(dateFilter) || dateFilter === "All") {
      return selectedRoundSchedule;
    }
    return filter(
      (s) =>
        format(utcToZonedTime(s.pairingTime, "Asia/Hong_Kong"), "EEEE") ===
        dateFilter,
      selectedRoundSchedule,
    );
  };

  return (
    <>
      {(isEmpty(dateFilter) || dateFilter === "All") &&
        _.size(selectedRoundSchedule) > 0 && (
          <Box my={2}>
            {violations.length === 0 ? (
              <Alert severity="success">
                All teams have the correct number of appearances, face unique
                opponents, are not scheduled more than twice in the same day,
                and have no jurisdiction pairing violations.
              </Alert>
            ) : (
              <>
                {violations.some((v) => !v.isWarning) && (
                  <Alert
                    severity="error"
                    style={{ marginBottom: 8, cursor: "pointer" }}
                    onClick={scrollToValidation}
                  >
                    {violations.filter((v) => !v.isWarning).length} team pairing
                    violation(s) found.
                  </Alert>
                )}
                {violations.every((v) => v.isWarning) &&
                  violations.length > 0 && (
                    <Alert
                      severity="warning"
                      style={{ marginBottom: 8, cursor: "pointer" }}
                      onClick={scrollToValidation}
                    >
                      {violations.length} team pairing warning(s) to review — no
                      team violations found.
                    </Alert>
                  )}
              </>
            )}
          </Box>
        )}
      <div style={{ height: 700, width: "100%" }}>
        <DataGridPro
          rows={getFilteredSchedule(selectedRoundSchedule, dateFilter)}
          columns={columns}
          onColumnVisibilityModelChange={handleColumnVisibilityChange}
          disableSelectionOnClick
          initialState={{
            sorting: {
              sortModel: [{ field: "pairingDateTime", sort: "asc" }],
            },
          }}
          loading={isLoading}
          components={{
            Toolbar: CustomToolbar,
          }}
        />
      </div>
      {(isEmpty(dateFilter) || dateFilter === "All") &&
        _.size(selectedRoundSchedule) > 0 &&
        violations.length > 0 && (
          <Box mt={2} ref={validationSectionRef}>
            <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
              <strong>Team Pairing Validation</strong>
            </Typography>
            <div style={{ width: "100%" }}>
              <DataGridPro
                autoHeight
                rows={violations}
                columns={violationColumns}
                disableSelectionOnClick
                hideFooter
                density="compact"
                getRowHeight={() => "auto"}
                getRowClassName={(params) =>
                  params.row.isWarning
                    ? "pairing-warning-row"
                    : "pairing-violation-row"
                }
                sx={{
                  "& .pairing-warning-row": { backgroundColor: "#fff9c4" },
                  "& .pairing-violation-row": {
                    backgroundColor: "rgb(253, 237, 237)",
                    color: "rgb(95, 33, 32)",
                  },
                }}
              />
            </div>
          </Box>
        )}
    </>
  );
};

export default connect(
  (state, ownProps) => ({
    user: state.user,
    currentMoot: state.moot.currentMoot,
    dateFilter: ownProps.dateFilter,
    oralArgumentSchedule: ownProps.oralArgumentSchedule,
    allArbitrators: ownProps.allArbitrators,
    onPairingDeleted: ownProps.onPairingDeleted,
  }),
  {
    runPairing,
    getCurrentMoot,
    getSelectedRoundSchedule: adminSelectors.getSelectedRoundSchedule,
    getOralArgumentSchedule: getCompleteOralArgumentSchedule,
    deletePairing,
  },
)(ScheduleTable);
